import { useState, useCallback, useRef, type ReactNode } from "react";
import { Formik, Form, Field } from "formik";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

interface RuleBreakDialogProps {
  open: boolean;
  ruleName: string;
  onConfirm: (note: string) => void;
  onCancel: () => void;
}

function RuleBreakDialog({ open, ruleName, onConfirm, onCancel }: RuleBreakDialogProps) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />

      {/* Dialog */}
      <div className="relative bg-app-surface border border-app-border rounded-xl shadow-xl max-w-sm w-full p-4 md:p-6 space-y-4">
        <div className="flex items-center gap-2">
          <span className="text-2xl">⚠️</span>
          <h2 className="text-body font-bold text-app-text">Rule Break</h2>
        </div>

        <p className="text-info text-app-text-muted">
          Reporting a break for: <strong className="text-app-text">{ruleName}</strong>
        </p>

        <Formik
          initialValues={{ note: "" }}
          validate={(values) => {
            const errors: { note?: string } = {};
            if (!values.note.trim()) {
              errors.note = "A note is required";
            }
            return errors;
          }}
          onSubmit={(values) => {
            onConfirm(values.note.trim());
          }}
        >
          {({ errors, touched }) => (
            <Form className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="break-note" className="text-app-text">
                  What happened?
                </Label>
                <Field
                  as={Textarea}
                  id="break-note"
                  name="note"
                  placeholder="Describe what happened..."
                  rows={3}
                  className="rounded-xl resize-none"
                />
                {errors.note && touched.note && (
                  <p className="text-sm text-app-danger">{errors.note}</p>
                )}
              </div>

              <div className="flex gap-2 justify-end pt-1">
                <Button type="button" variant="outline" onClick={onCancel}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-app-danger hover:bg-app-danger/80"
                >
                  Report Break
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}

/**
 * Hook for managing rule break dialog state.
 * Returns an openBreakDialog function that resolves with the note text or null.
 */
export function useRuleBreakDialog() {
  const [state, setState] = useState<{
    open: boolean;
    ruleName: string;
  }>({
    open: false,
    ruleName: "",
  });

  const resolveRef = useRef<((value: string | null) => void) | null>(null);

  const openBreakDialog = useCallback((ruleName: string): Promise<string | null> => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setState({ open: true, ruleName });
    });
  }, []);

  const handleConfirm = useCallback((note: string) => {
    resolveRef.current?.(note);
    resolveRef.current = null;
    setState({ open: false, ruleName: "" });
  }, []);

  const handleCancel = useCallback(() => {
    resolveRef.current?.(null);
    resolveRef.current = null;
    setState({ open: false, ruleName: "" });
  }, []);

  const dialog: ReactNode = (
    <RuleBreakDialog
      open={state.open}
      ruleName={state.ruleName}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  );

  return { openBreakDialog, dialog };
}
